import React, { useEffect, useRef, useState } from 'react';
import {
  useStageView, useTerritoryStage, useTerritoryStageState,
} from '../terrain3d/TerritoryStage';
import {
  StepPanel, StepCollapsedBar, StepNavRow,
  authorDefaults, panelContentInsets, useBandHeight,
  RAIL_WIDTH, PANEL_MARGIN,
} from './StepPanel';
import { nextLabel } from '../gameFlow';
import './TerritoryIntroPage.css';

// Facteurs de lisibilité de l'ÉCRAN d'atelier. Le module est projeté devant
// une salle : à 12 px, ce qui est écrit est illisible au-delà du deuxième
// rang. La taille utile dépend de la salle et du vidéoprojecteur, elle ne peut
// pas être devinée à l'écriture de l'atelier — c'est un réglage de séance, à
// poser une fois en arrivant.
const UI_SCALES = [
  { id: 'normal', label: 'Normal', value: 1 },
  { id: 'large', label: 'Salle', value: 1.35 },
  { id: 'xlarge', label: 'Grande salle', value: 1.7 },
];

/**
 * Réglages de l'ÉCRAN, à ne pas confondre avec le ⚙ de la carte (couches,
 * brume, rotation) qui vit dans la colonne des légendes, à droite. Ce sont
 * deux sujets sans rapport : l'un règle ce que la carte montre, l'autre règle
 * la façon dont la salle le voit.
 *
 * Il est posé À GAUCHE, où la carte est le plus souvent dégagée — la droite
 * est déjà prise par les légendes. Quand l'étape est en colonne, il se range
 * juste après elle plutôt que dessous : `offsetLeft` suit la disposition.
 */
function ScreenSettings({ uiScale, onUiScaleChange, offsetLeft }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  return (
    <div className="territory-intro-screenbtn-wrap" ref={wrapRef} style={{ left: offsetLeft }}>
      <button
        type="button"
        className={`territory-intro-screenbtn${open ? ' is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        title="Réglages de l'écran (lisibilité, raccourcis)"
        aria-label="Réglages de l'écran"
      >
        Aa
      </button>

      {open && (
        <div className="territory-intro-screenmenu">
          <span className="territory-intro-menu-title">Réglages de l'écran</span>
          <span className="territory-intro-menu-hint">
            Taille de l'affichage, à régler selon la salle et le vidéoprojecteur.
          </span>
          <div className="territory-intro-seg" role="group" aria-label="Taille de l'affichage">
            {UI_SCALES.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`territory-intro-seg-btn${uiScale === option.value ? ' is-active' : ''}`}
                onClick={() => onUiScaleChange(option.value)}
                aria-pressed={uiScale === option.value}
              >
                {option.label}
              </button>
            ))}
          </div>
          {/* L'animateur présente debout, face à la salle : ces raccourcis sont
              ce qui lui évite de viser un bouton au trackpad en parlant. */}
          <p className="territory-intro-menu-shortcuts">
            <span>← →</span> étape précédente / suivante<br />
            <span>Échap</span> masquer ou afficher le panneau
          </p>
        </div>
      )}
    </div>
  );
}

// Croquis d'une disposition : le cadre de l'écran, et en plein la surface que
// le panneau y occupe. C'est la seule chose que l'animateur a besoin de savoir
// pour choisir — « bandeau bas » ou « grand panneau » sont des mots, un aplat
// posé au bon endroit est la réponse.
function LayoutSketch({ shape }) {
  const fill = {
    rail: { x: 3, y: 3, width: 9, height: 18 },
    bottom: { x: 3, y: 14, width: 28, height: 7 },
    overlay: { x: 6, y: 5, width: 22, height: 14 },
    hidden: { x: 3, y: 16, width: 9, height: 5 },
  }[shape];
  return (
    <svg viewBox="0 0 34 24" className="territory-intro-layout-sketch" aria-hidden="true">
      <rect x="0.5" y="0.5" width="33" height="23" rx="3" className="territory-intro-layout-map" />
      <rect {...fill} rx="1.5" className="territory-intro-layout-fill" />
    </svg>
  );
}

// Dispositions proposées à l'animateur pendant la séance. Elles recouvrent les
// deux réglages internes d'un coup : la TAILLE du panneau et son REPLI. C'est
// ainsi que ça se pense en salle — « je veux la carte en entier », pas « je
// veux replier un panneau dont la taille reste en colonne ».
const PANEL_LAYOUTS = [
  {
    id: 'rail',
    label: 'Colonne à gauche',
    hint: 'La carte garde l\'essentiel du cadre.',
    size: 'rail',
    collapsed: false,
  },
  {
    id: 'bottom',
    label: 'Bandeau bas',
    hint: 'Toute la largeur en bas, carte entière au-dessus.',
    size: 'bottom',
    collapsed: false,
  },
  {
    id: 'overlay',
    label: 'Grand panneau',
    hint: 'Le contenu au premier plan, format diapo.',
    size: 'overlay',
    collapsed: false,
  },
  {
    id: 'hidden',
    label: 'Carte plein écran',
    // `size: null` = on ne touche pas à la taille choisie : elle est retrouvée
    // telle quelle quand le panneau revient.
    hint: 'Panneau masqué, seule la navigation reste (Échap).',
    size: null,
    collapsed: true,
  },
];

/**
 * Bouton de disposition : un croquis de l'état courant, qui déplie la liste
 * des quatre dispositions. Il remplace deux boutons texte (« Agrandir »,
 * « Masquer ») qui, à partir de quatre états, ne pouvaient plus dire ni où on
 * est ni où on va.
 *
 * `dropUp` parce que le même sélecteur sert dans la barre du bas quand le
 * panneau est masqué : la liste doit alors s'ouvrir vers le haut.
 */
function LayoutPicker({ currentId, onSelect, dropUp = false }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Fermeture au clic ailleurs : sans ça, la liste reste ouverte par-dessus la
  // carte pendant qu'on manipule le relief.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const current = PANEL_LAYOUTS.find((l) => l.id === currentId) || PANEL_LAYOUTS[0];

  return (
    <div className="territory-intro-menu-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`territory-intro-layout-btn${open ? ' is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        title={`Disposition de l'écran : ${current.label}`}
      >
        <LayoutSketch shape={current.id} />
        <span className="territory-intro-caret" aria-hidden="true" />
      </button>

      {open && (
        <div className={`territory-intro-layout-menu${dropUp ? ' is-up' : ''}`} role="menu">
          <span className="territory-intro-menu-title">Disposition de l'écran</span>
          {PANEL_LAYOUTS.map((layout) => (
            <button
              key={layout.id}
              type="button"
              role="menuitemradio"
              aria-checked={layout.id === current.id}
              className={`territory-intro-layout-option${layout.id === current.id ? ' is-active' : ''}`}
              onClick={() => { onSelect(layout); setOpen(false); }}
            >
              <LayoutSketch shape={layout.id} />
              <span className="territory-intro-layout-option-text">
                <span className="territory-intro-layout-option-label">{layout.label}</span>
                <span className="territory-intro-layout-option-hint">{layout.hint}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Rendu joueur du module d'intro territoire 3D personnalisable : parcourt
// content.steps localement (cheminement lineaire, Precedent/Suivant font
// avancer/reculer l'index) et ne remonte au moteur de jeu (onDecisionSelect)
// qu'a la derniere etape — aucune decision/score en jeu, comme une page
// "context".
//
// LA CARTE N'EST PLUS A LUI. Le module montait `TerrainViewer` ; il DECLARE
// desormais la vue qu'il veut a l'ecran de territoire de la seance (cf.
// terrain3d/TerritoryStage.js), qui detient l'unique instance et la garde
// construite entre deux pages. Le geste est le meme — les memes champs d'etape
// partent au meme endroit — mais la scene survit a la fin du module.
const TerritoryIntroPage = ({ pageData, gameStatus, onDecisionSelect, displayButton = true, endsGame = false }) => {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  // Choix du joueur pour L'ETAPE COURANTE, prioritaires sur les defauts de
  // l'auteur — remis a null (retour au defaut) a chaque changement d'etape.
  const [manualSize, setManualSize] = useState(null);
  const [manualCollapsed, setManualCollapsed] = useState(null);

  const stage = useTerritoryStage();
  // Deux etats qui vivaient ici et appartiennent en realite a l'ecran :
  //  - `uiScale` est un reglage de SEANCE (la salle, le videoprojecteur) et
  //    retombait a 1 des qu'on quittait le module ;
  //  - `selection` est ce qui est ouvert SUR LA CARTE (station, commune, point
  //    climatique), dont le panneau d'etape a besoin pour redessiner un
  //    graphique regle sur « suivre la selection ».
  const { uiScale, selection } = useTerritoryStageState();

  const content = pageData.content || {};
  const territoryId = content.territory_id;
  const availableLayers = content.layers || [];
  const fogEnabled = content.fog_enabled !== false;
  const steps = content.steps || [];
  const currentStep = steps[activeStepIndex];

  const finishModule = () => {
    onDecisionSelect({ page_id: pageData._id, page_type: 'territory_intro' });
  };

  const goToStep = (index) => {
    setActiveStepIndex(index);
    setManualSize(null);
    setManualCollapsed(null);
  };

  const handleNextClick = () => {
    if (activeStepIndex < steps.length - 1) {
      goToStep(activeStepIndex + 1);
      return;
    }
    // Partie terminée : on garde la navigation ENTRE les étapes — le module
    // reste consultable pendant le débrief — mais la dernière ne sort plus du
    // module, il n'y a plus de suite.
    if (displayButton) finishModule();
  };

  const handlePreviousClick = () => {
    if (activeStepIndex > 0) goToStep(activeStepIndex - 1);
  };

  // Etat du panneau : defaut voulu par l'auteur pour cette etape, corrige par
  // ce que l'animateur a fait depuis. Calcule AVANT le retour anticipe pour
  // rester lisible par le raccourci clavier ci-dessous.
  const defaults = authorDefaults(currentStep?.panel_mode);
  const size = manualSize || defaults.size;
  const collapsed = manualCollapsed === null ? defaults.collapsed : manualCollapsed;

  // Hauteur reelle du bandeau bas, mesuree : c'est elle que le viewer
  // retranche pour recentrer sa camera (cf. useBandHeight).
  const [panelRef, bandHeight] = useBandHeight(!collapsed && size === 'bottom');
  const contentInsets = panelContentInsets({ size, collapsed, bandHeight });

  // CE QUE LE MODULE DEMANDE A L'ECRAN. Un objet, pas des props : l'ecran vit
  // au-dessus de la page et lui survit. Calcule AVANT le retour anticipe
  // ci-dessous, parce qu'un hook ne peut pas etre conditionnel — un module sans
  // etape passe simplement `null`, ce qui ne revendique rien.
  //
  // `layers` est ce qui doit etre CONSTRUIT, `active_layers` ce qui est ALLUME.
  // Le premier ne bouge pas de la seance (le changer reconstruirait la scene),
  // le second change a chaque etape et ne coute qu'une bascule de visibilite.
  useStageView(pageData._id, currentStep && territoryId ? {
    territory_id: territoryId,
    layers: availableLayers,
    mode: 'stage',
    active_layers: currentStep.active_layers || [],
    layer_settings: currentStep.layer_settings || {},
    camera: currentStep.camera || null,
    fog_enabled: fogEnabled,
    points: currentStep.points_of_interest?.points || [],
    content_insets: contentInsets,
  } : null);

  // PILOTAGE AU CLAVIER. L'animateur présente debout, face à la salle : viser
  // un bouton au trackpad tout en parlant est le geste le plus fragile de la
  // séance. Flèches pour avancer/reculer, Échap pour masquer le panneau et
  // rendre la carte plein cadre — les raccourcis d'un logiciel de
  // présentation, parce que c'est ce que ce module est devenu.
  useEffect(() => {
    const onKeyDown = (event) => {
      // Ne jamais voler une frappe destinée à un champ de saisie (le module
      // est aussi rendu dans l'aperçu du builder).
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || event.target?.isContentEditable) return;
      if (event.key === 'ArrowRight' || event.key === 'PageDown') {
        event.preventDefault();
        handleNextClick();
      } else if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault();
        handlePreviousClick();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setManualCollapsed(!collapsed);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  // Monte hors d'un ecran de territoire : erreur de cablage, pas de donnee. Le
  // module dessinerait ses panneaux au-dessus du vide, ce qui est le pire des
  // symptomes — ca ressemble a un probleme de carte alors que c'est un
  // provider manquant. On le dit, et on laisse une sortie.
  if (!stage) {
    console.error(
      "[TerritoryIntroPage] rendu hors d'un TerritoryStageProvider : "
      + "la carte n'a nulle part ou vivre."
    );
    return (
      <div className="territory-intro-page territory-intro-page-empty">
        <div className="territory-intro-empty">
          <p>La carte du territoire n'est pas disponible dans ce contexte.</p>
          {displayButton && <button className="blue-button" onClick={finishModule}>{nextLabel(endsGame)}</button>}
        </div>
      </div>
    );
  }

  // Module mal configure (aucune etape) : ne bloque pas la partie, on passe
  // directement a la suite plutot que d'afficher un ecran vide sans issue.
  if (!currentStep) {
    return (
      <div className="territory-intro-page territory-intro-page-empty">
        <div className="territory-intro-empty">
          <p>Ce module n'a pas d'étape configurée.</p>
          {displayButton && <button className="blue-button" onClick={finishModule}>{nextLabel(endsGame)}</button>}
        </div>
      </div>
    );
  }

  // Disposition courante, telle que l'animateur la voit : masque l'emporte sur
  // la taille, qui est conservee en coulisses pour le retour.
  const currentLayoutId = collapsed ? 'hidden' : size;
  const applyLayout = (layout) => {
    if (layout.size) setManualSize(layout.size);
    setManualCollapsed(layout.collapsed);
  };

  const navRow = (
    <StepNavRow
      isFirst={activeStepIndex === 0}
      isLast={activeStepIndex === steps.length - 1}
      onPrevious={handlePreviousClick}
      onNext={handleNextClick}
    />
  );

  // `key` : changer d'étape referme la liste restée ouverte — au clavier,
  // aucun clic ne serait passé à côté pour la fermer. `dropUp` dans la barre
  // du bas, où la liste sortirait de l'écran en s'ouvrant vers le bas.
  const layoutPicker = (dropUp) => (
    <LayoutPicker
      key={activeStepIndex}
      currentId={currentLayoutId}
      onSelect={applyLayout}
      dropUp={dropUp}
    />
  );

  return (
    /* La carte n'est plus ici : elle est peinte par l'ecran de territoire, un
       calque pose SOUS cette page (cf. terrain3d/TerritoryStage.js). D'ou le
       `pointer-events: none` de la feuille de style sur ce conteneur — sans
       lui, cette boite pleine fenetre intercepterait l'orbite et les clics
       destines au relief. */
    <div className="territory-intro-page">
      {/* En colonne, le panneau d'étape occupe tout le bord gauche : le bouton
          se range juste après lui. Dans les autres dispositions, il revient au
          coin. */}
      <ScreenSettings
        uiScale={uiScale}
        onUiScaleChange={stage.setUiScale}
        offsetLeft={!collapsed && size === 'rail' ? RAIL_WIDTH + PANEL_MARGIN + 10 : 16}
      />

      {/* Panneau masqué : pas de bouton « Afficher l'étape » — le sélecteur
          ramène le panneau ET dit où il revient, ce que le bouton ne faisait
          qu'à moitié. */}
      {collapsed ? (
        <StepCollapsedBar
          steps={steps}
          stepIndex={activeStepIndex}
          nav={navRow}
          actions={layoutPicker(true)}
        />
      ) : (
        /* Une seule commande dans l'en-tête d'étape : où tombe le panneau. Tout
           le reste (lisibilité, brume, couches) est un réglage de séance, posé
           une fois, et vit à côté de la carte. */
        <StepPanel
          step={currentStep}
          size={size}
          gameStatus={gameStatus}
          steps={steps}
          stepIndex={activeStepIndex}
          nav={navRow}
          actions={layoutPicker(false)}
          panelRef={panelRef}
          territoryId={territoryId}
          selection={selection}
        />
      )}
    </div>
  );
};

export default TerritoryIntroPage;
